import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHybridAuth } from '../../contexts/HybridAuthContext';
import { useWhitelabel } from '../../contexts/WhitelabelContext';
import { toast } from 'react-hot-toast';
import { authService } from '../../services/authService';

export default function TestSignIn() {
  const navigate = useNavigate();
  const { refreshUser } = useHybridAuth();
  const { branding } = useWhitelabel();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }

    setIsLoading(true);

    try {
      const data = await authService.login(email, password);
      if (!data) {
        toast.error('Invalid credentials');
        return;
      }
      toast.success('Signed in successfully!');

      await refreshUser();

      if (data.user && (data.user.role === 'super_admin' || data.user.role === 'admin')) {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error: any) {
      console.error('[Login] Sign in error:', error);
      toast.error(error?.message || 'Failed to sign in. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex flex-col items-center justify-center mb-6">
            {branding.hasWhitelabel && branding.companyLogo ? (
              <>
                <img
                  className="max-h-20 max-w-[300px] object-contain mb-4"
                  src={branding.companyLogo}
                  alt={branding.companyName}
                  style={{ maxHeight: '80px', maxWidth: '300px' }}
                />
                <h2 className="text-2xl font-bold text-white text-center">
                  {branding.companyName}
                </h2>
              </>
            ) : (
              <>
                <img
                  className="h-16 w-auto mb-3"
                  src="/assets/img/optimancer_logo.svg"
                  alt={branding.companyName}
                />
                <h2 className="text-2xl font-bold text-white text-center">
                  {branding.companyName}
                </h2>
              </>
            )}
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-slate-400">
            Access your AIO analysis dashboard
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-slate-700 placeholder-slate-500 text-white bg-slate-800 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-slate-700 placeholder-slate-500 text-white bg-slate-800 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Password"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>

          <div className="flex items-center justify-between">
            <a
              href="/forgot-password"
              className="text-sm font-medium text-blue-400 hover:text-blue-300"
            >
              Forgot your password?
            </a>
          </div>

          <div className="text-center">
            <p className="text-sm text-slate-400">
              Don't have an account?{' '}
              <a href="/sign-up" className="font-medium text-blue-400 hover:text-blue-300">
                Sign up
              </a>
            </p>
          </div>

        </form>
      </div>
    </div>
  );
}
